<template>
  <oie-expandable-item
    v-element-loader="{ endpoint: 'checkout/getDeliveryMethods' }"
    :component-index="componentIndex"
    :heading="t('shipmentTypeHeader')"
    :class="['expandable-item--seperate-items', { 'expandable-item--is-disabled': !isActiveSection }]">
    <template slot="pocket">
      <oie-message-box v-if="showEodMessage" class="message-box--compact" v-html="t('eodFreeShippingMessageLabel')">
      </oie-message-box>
      <oie-message-box
        v-else-if="showleftToFreeShipping"
        class="message-box--compact"
        color="orange"
        v-html="leftToFreeShippingMessage">
      </oie-message-box>
      <oie-message-box v-else-if="showFreeShipping && !isOrderRequestVisitorState" class="message-box--compact" v-html="freeShippingMessage">
      </oie-message-box>
    </template>
    <template v-if="shippingMethods && shippingMethods.length > 0" slot="pocket">
      <oie-message-box
        v-if="shippingMethods[0] && shippingMethods[0].message"
        class="message-box--compact"
        color="orange"
        v-html="shippingMethods[0].message">
      </oie-message-box>
    </template>
    <template v-for="shippingType in shippingMethods">
      <div v-if="shippingType.items && shippingType.items.length > 0" :key="shippingType.shippingType" class="form-container">
        <div class="form-container__top">
          <h3>
            {{ t(`shipmentMethodTitle${shippingType.shippingType}`) }}
          </h3>

          <p>
            {{ t(`shipmentMethodDescription${shippingType.shippingType}`) }}
          </p>
        </div>

        <div v-for="(method, m) in shippingType.items" :key="`${m}-${method.optionId}`">
          <oie-base-radio
            :id="`${method.optionId}_i`"
            v-model="selectedShipping"
            :use-chip-text="method.hasShippingAgreement"
            :chip-text="t('shippingAgreementExistsChipText')"
            name="shipping-type"
            :value="method.optionId"
            class="checkout-shipping-type__radio"
            :is-checked="selectedShipping === method.optionId"
            :label-text="method.optionTitle + ' ' + method.optionSubTitle"
            :use-sub-text="true">
            <span class="radio__label-sub-text-container">
              {{ method.descriptionCaption }} {{ method.descriptionText }}

              <span class="radio__label-sub-text-price">
                {{ formatPrice(getDeliveryMethodPrice(method)) }}
              </span>
            </span>
          </oie-base-radio>

          <div v-if="selectedShipping === method.optionId && method.hasShippingAgreement" class="checkout-shipping-type__shipping-agreement">
            <oie-base-checkbox
              v-model="method.useShippingAgreement"
              is-for="use-shipping-agreement"
              :text="t('useShippingAgreementText')"></oie-base-checkbox>

            <oie-base-input
              id="shipping-agreement-no"
              :value="method.shippingAgreement"
              :label-text="t('shippingtAgreementNumberLabel')"
              :is-readonly="true"></oie-base-input>
          </div>

          <div
            v-if="hasAgents(method)"
            :id="'checkout-shipping-types-agents-dropdown'"
            v-click-outside="closeDropdown"
            class="custom-dropdown checkout-shipping-type__agents">
            <button
              :aria-expanded="isDropdownOpen"
              aria-controls="agents-dropdown-list"
              :aria-label="t('shipmentChooseAgentAria')"
              :class="['custom-dropdown__toggler', { 'custom-dropdown__toggler--is-open': isDropdownOpen }]"
              @click="toggleDropdown()">
              <template v-if="!selectedAgent">
                {{ t('shipmentChooseAgent') }}
              </template>

              <template v-else>
                {{ selectedAgent.name }} {{ selectedAgent.address1 }}
              </template>

              <oie-use-svg svg="caret"></oie-use-svg>
            </button>
            <div
              v-if="isDropdownOpen"
              :id="'agents-dropdown-list'"
              :class="['custom-dropdown__content']">
              <fieldset class="custom-dropdown__content-inner" @keydown.esc.stop="closeDropdown" @keydown.enter.stop="closeDropdown">
                <legend class="sr-only">
                  {{ t('shipmentChooseAgent') }}
                </legend>
                <ul>
                  <li v-for="agent of method.agents" :key="agent.agentId" :class="['custom-dropdown__row-action']">
                    <oie-base-radio-checkbox
                      v-model="selectedAgent"
                      :value="agent"
                      :label="`${agent.name} ${agent.address1}`"
                      name="shipping-agent">
                    </oie-base-radio-checkbox>
                  </li>
                </ul>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="cart.splitOrder && !isOrderRequestVisitorState" class="form-container">
      <div class="form-container__top">
        <h3>
          {{ t('splitShipmentInfo') }}
        </h3>
      </div>

      <oie-base-radio
        id="splitshipping-yes"
        v-model="partDelivery"
        name="split-shipping"
        :value="true"
        :is-checked="partDelivery === true"
        :label-text="t('shipmentConfirmSplitOrderLabel')"></oie-base-radio>

      <oie-base-radio
        id="splitshipping-no"
        v-model="partDelivery"
        name="split-shipping"
        :value="false"
        :is-checked="partDelivery === false"
        :label-text="t('shipmentDeclineSplitOrderLabel')"
        data-pw="splitshipping-no"></oie-base-radio>
    </div>

    <validation-observer
      v-if="!isDigital"
      ref="notificationForm"
      tag="form"
      class="form-container notification-form">
      <div class="form-container__top">
        <h3>
          {{ t('labelNotificationTitle') }}
        </h3>
      </div>

      <validation-provider
        v-if="!noemail"
        v-slot="{errors}"
        rules="required"
        slim>
        <oie-base-input
          id="notification-email"
          v-model="notificationEmail"
          :textarea="false"
          :label-text="`${t('labelNotificationEmail')}*`"
          :placeholder="t('placeholderNotificationEmail')"
          :max-length="100"
          css-classes="checkout-actions__input"
          :error-messages="errors"
          @blur="setNotificationPayload()"></oie-base-input>
      </validation-provider>

      <validation-provider
        v-if="mobileIsMandatory"
        v-slot="{errors}"
        :rules="createMobileValidationRule"
        slim>
        <oie-base-input
          id="notification-phone"
          v-model="formatingProperties.phone"
          :label-text="mobileValidationRuleHasRequired ? `${t('labelNotificationPhone')}*` : t('labelNotificationPhone')"
          :placeholder="t('placeholderNotificationPhone')"
          :prefix-value="formatingProperties.callingCode"
          :error-messages="errors"
          @blur="formatNotificationPhone()"></oie-base-input>
      </validation-provider>
    </validation-observer>

    <div v-if="!isStepActive(componentIndex)" class="form-container">
      <div class="form-container__top">
        <p v-if="!isOrderRequestVisitorState">
          {{ t('shipmentTypePickupLabel') }}
        </p>
      </div>

      <oie-checkout-next-step
        v-show="!isStepActive(componentIndex)"
        :component-index="componentIndex"
        :text="t('wizardNextStep')"
        :disabled="!isNextStepAvailable"
        :push-ga-data-handler="pushGaData"
        :playwright="'checkout-next-step-shipping'"></oie-checkout-next-step>
    </div>
  </oie-expandable-item>
</template>

<script>
import httpService from '@/core/http-service'
import nextStepMixin from '@/mixins/checkout-next-step.js'
import gtm from '@/core/enhanced-ecommerce-gtm'
import utils from '@/core/utils'
import { CHECKOUT_RESET_SHIPPING, CHECKOUT_SIGN_UP, CHECKOUT_RELOAD_SHIPPING } from '@/variables/checkout-event-keys'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  mixins: [nextStepMixin],
  props: {
    eventKey: {
      type: String,
      default: null
    },
    eventSplitKey: {
      type: Boolean,
      default: false
    },
    shippingAddress: {
      type: Object,
      default: () => null
    },
    customer: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    selectedShipping: null,
    previousSelectedShipping: null,
    selectedMethodType: null,
    partDelivery: null,
    shippingMethods: Array,
    shippingAgents: Array,
    isDropdownOpen: false,
    selectedAgent: null,
    selectedAgentId: null,
    isNextStepAvailable: false,
    loading: true,
    shippingMethodsFake: [],
    isPageloadDone: false,
    isSelectedResetFromEvent: false,
    notificationEmail: null,
    notificationPhone: null,
    noemail: null,
    mobileRule: null,
    formatingProperties: {
      callingCode: null,
      phone: null
    },
    useShippingAgreement: false
  }),
  computed: {
    ...mapGetters('commerce', ['cart']),
    ...mapGetters('checkout', ['checkoutState', 'isOrderRequestVisitorState']),
    ...mapGetters(['enableGtm']),
    showleftToFreeShipping() {
      return !this.showEodMessage && this.cart.leftToFreeShipping > 0 && this.selectedShipping && !this.useShippingAgreement
    },
    showEodMessage() {
      return this.cart.eodEnabled && this.selectedShipping && !this.useShippingAgreement
    },
    leftToFreeShippingMessage() {
      return this.t('shipmentMessageLabel').replace('{0}', this.formatPrice(this.cart.leftToFreeShipping)).replace('{1}', this.formatPrice(this.cart.ordinaryShippingFee))
    },
    showFreeShipping() {
      return !this.showEodMessage && this.cart.shippingFee === 0 && this.cart.ordinaryShippingFee > 0 && this.selectedShipping && !this.useShippingAgreement
    },
    freeShippingMessage() {
      return this.t('freeShipmentMessageLabel').replace('{0}', this.formatPrice(this.cart.ordinaryShippingFee))
    },
    partDeliverySelected() {
      return this.partDelivery === false || this.partDelivery === true
    },
    showSelectedAgent() {
      return this.selectedAgent
    },
    createMobileValidationRule() {
      var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
      var method = _.find(shippingMethodsFlat, (o) => { return o && o.optionId === this.selectedShipping })
      if (method && !method.isPhoneMandatory) {
        if (this.mobileRule) {
          return `mobile:${this.mobileRule}`
        }
        return ''
      } else {
        if (this.mobileRule) {
          return `required|mobile:${this.mobileRule}`
        }
        return 'required|numeric'
      }
    },
    mobileValidationRuleHasRequired() { return _.includes(this.createMobileValidationRule, 'required') },
    mobileIsMandatory() {
      var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
      var method = _.find(shippingMethodsFlat, (o) => { return o && o.optionId === this.selectedShipping })
      return method && method.isPhoneMandatory
    },
    isDigital() {
      var shippingMethodsFilter = _.filter(this.shippingMethods, (s) => { return s.items && s.items.length > 0 })
      return shippingMethodsFilter.length === 1 && shippingMethodsFilter[0].shippingType === 'Digital' && !!shippingMethodsFilter[0].items.length
    }
  },
  watch: {
    'shippingMethods': {
      handler: function (obj) {
        if (this.selectedShipping && this.partDeliverySelected) {
          var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
          var method = _.find(shippingMethodsFlat, (o) => { return o.optionId === this.selectedShipping })
          if(method && this.useShippingAgreement !== method.useShippingAgreement) {
            this.useShippingAgreement = method.useShippingAgreement
            this.publish(this.eventKey, { selectedShipping: this.selectedShipping, selectedAgentId: this.selectedAgentId, useShippingAgreement: method.useShippingAgreement, shippingAgreement: method.shippingAgreement })
          }
        }
      },
      deep: true
    },
    isActiveSection(value) {
      if (this.selectedShipping && this.partDeliverySelected) {
        var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
        var method = _.find(shippingMethodsFlat, (o) => { return o.optionId === value })
        this.isNextStepAvailable = true
        if (method) {
          this.publish(this.eventKey, { selectedShipping: this.selectedShipping, selectedAgentId: this.selectedAgentId, useShippingAgreement: method.useShippingAgreement, shippingAgreement: method.shippingAgreement })
        }
      }
    },
    partDeliverySelected(value) {
      var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
      var method = _.find(shippingMethodsFlat, (o) => { return o.optionId === this.selectedShipping })

      this.isNextStepAvailable = value && this.selectedShipping && this.selectedMethodType && (this.selectedMethodType !== 'agent' || this.selectedAgent || method.suppressAgentList)
    },
    selectedShipping(value) {
      var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
      var method = _.find(shippingMethodsFlat, (o) => { return o.optionId === value })

      if (method && !method.isPhoneMandatory) {
        this.notificationPhone = null
        this.setNotificationPayload()
      } else if (!this.notificationPhone) {
        this.formatNotificationPhone()
      }

      if (method === null || method === undefined) {
        return
      }

      this.useShippingAgreement = method.useShippingAgreement

      if (this.isSelectedResetFromEvent) {
        this.publish(this.eventKey, { selectedShipping: this.selectedShipping, selectedAgentId: this.selectedAgentId, useShippingAgreement: method.useShippingAgreement, shippingAgreement: method.shippingAgreement })
        this.isSelectedResetFromEvent = false

        return
      }

      if ((this.selectedShipping !== this.previousSelectedShipping) || ((this.selectedShipping === this.previousSelectedShipping) && !this.isPageloadDone)) {
        if (this.isPageloadDone) {
          this.selectedAgent = null
          this.selectedAgentId = null
        }
        this.previousSelectedShipping = this.selectedShipping
        if (this.enableGtm) {
          const info = {}
          if (value) {
            _.each(this.shippingMethods, (m) => {
              const shipping = _.find(m.items, (i) => i.optionId === value)
              if (shipping) info.shipping_tier = shipping.optionTitle
            })
          }
          gtm.addCheckoutStep('add_shipping_info', this.cart, info)
        }

        if (!this.isPageloadDone) {
          this.isPageloadDone = true
        }
      }

      this.selectedMethodType = method.deliveryType

      if (method.deliveryType === 'agent' && !method.suppressAgentList) {
        this.isNextStepAvailable = false

        if (this.selectedAgentId === null && method.agents.length > 0) {
          this.selectedAgentId = method.agents[0].agentId
        }

        if (this.selectedAgentId !== null) {
          this.selectedAgent = _.find(method.agents, (x) => { return x.agentId === this.selectedAgentId })

          if(this.selectedAgent) {
            this.isNextStepAvailable = this.partDeliverySelected
          } else {
            this.isNextStepAvailable = false
          }
        }
      } else if (method.optionId === '31') {
        this.isNextStepAvailable = true
        this.partDeliverySelected = true
      } else {
        this.selectedAgent = null
        this.selectedAgentId = null
        this.isNextStepAvailable = this.partDeliverySelected
      }
      this.setOrderRequestVisitor(method.deliveryTypeEnum === 3)

      this.publish(this.eventKey, { selectedShipping: this.selectedShipping, selectedAgentId: this.selectedAgentId, useShippingAgreement: method.useShippingAgreement, shippingAgreement: method.shippingAgreement })
    },
    selectedAgent(value) {
      if(value === undefined || value === null) {
        this.selectedAgentId = null
        return
      }

      var shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
      var method = _.find(shippingMethodsFlat, (o) => { return o.optionId === value.optionId })

      this.selectedAgentId = value.agentId
      this.publish(this.eventKey, { selectedShipping: this.selectedShipping, selectedAgentId: this.selectedAgentId, useShippingAgreement: method.useShippingAgreement, shippingAgreement: method.shippingAgreement })
      this.isNextStepAvailable = this.partDeliverySelected
    },
    partDelivery(value) {
      this.publish('checkout-part-delivery', value)
    },
    shippingAddress: {
      deep: true,
      handler() {
        if (this.shippingAddress.postalCode !== null && this.shippingAddress.countryCode !== null) {
          this.loadShippingMethods()
        }
      }
    },
    customer: {
      immediate: true,
      handler(value) {
        if (value.settings && value.settings.deliveryNotificationPhone) {
          const countryObject = this.clientModel.countryList.find(c => c.value === value.invoiceAddress.countryCode)
          this.formatingProperties.callingCode = countryObject.callingCode
          let phone = value.settings.deliveryNotificationPhone
          if (phone.startsWith('+')) {
            this.notificationPhone = phone
            this.formatingProperties.phone = utils.extractPhoneNumberFromFormating(phone, countryObject.callingCode)
          } else {
            this.notificationPhone = utils.savePhoneNumberWithFormating(phone, countryObject.callingCode)
            this.formatingProperties.phone = phone
          }
          this.mobileRule = countryObject.mobileRule
        }
        if (value.settings && value.settings.deliveryNotificationEmail !== null) {
          this.notificationEmail = value.settings.deliveryNotificationEmail
        }
        this.setNotificationPayload()
      }
    }
  },
  mounted() {
    // this.isOpen = this.isActiveSection ? true : null

    if (this.shippingAddress !== null && this.shippingAddress.postalCode !== null && this.shippingAddress.countryCode !== null) {
      this.getShippingMethods()
    }
    if (!this.cart.splitOrder) {
      this.partDelivery = false
    }
    if (this.isNextStepAvailable) {
      this.publish(`checkout-wizard-step-${this.componentIndex}`, this.componentIndex)
    }

    this.subscribe(CHECKOUT_RESET_SHIPPING, () => {
      this.resetShippingStep()
    })

    this.subscribe(CHECKOUT_RELOAD_SHIPPING, () => {
      this.getShippingMethods()
    })

    this.subscribe(CHECKOUT_SIGN_UP, (value) => {
      if (value.phone) {
        const countryObject = this.clientModel.countryList.find(c => c.value === value.countryCode)
        this.formatingProperties.callingCode = countryObject.callingCode
        if (!(this.customer && this.customer.settings && this.customer.settings.deliveryNotificationPhone !== null)) {
          if (value.phone.startsWith('+')) {
            this.notificationPhone = value.phone
            this.formatingProperties.phone = utils.extractPhoneNumberFromFormating(value.phone, countryObject.callingCode)
          } else {
            this.notificationPhone = utils.savePhoneNumberWithFormating(value.phone, countryObject.callingCode)
            this.formatingProperties.phone = value.phone
          }
        }
        this.mobileRule = countryObject.mobileRule
      }
      if (value.username && !(this.customer && this.customer.settings && this.customer.settings.deliveryNotificationEmail !== null)) {
        this.notificationEmail = value.username
      }
      this.noemail = value.noemail
      if (value.noemail && !(this.customer && this.customer.settings && this.customer.settings.deliveryNotificationEmail !== null)) {
        this.notificationEmail = null
      }
      this.setNotificationPayload()
    })
  },
  methods: {
    ...mapActions('checkout', ['setOrderRequestVisitor']),
    loadShippingMethods: _.debounce(function () {
      this.getShippingMethods()
    }, 1000),
    getShippingMethods() {
      httpService.get('checkout/getDeliveryMethods',
        {
          destinationCity: this.shippingAddress.city,
          destinationCountryCode: this.shippingAddress.countryCode,
          destinationStreetAddress: this.shippingAddress.addressLine1,
          destinationZipCode: this.shippingAddress.postalCode,
          pallet: this.cart.pallet,
          leakage: this.cart.leakage,
          grossWeight: this.cart.grossWeight,
          totalVolume: this.cart.totalVolume,
          orderValue: this.cart.totalPriceExShippingExSum,
          shippingGroup: this.cart.shippingGroup,
          isCompanyCustomer: this.shippingAddress.isCompanyCustomer,
          heaviestItemWeight: this.cart.heaviestItemWeight,
          longestItem: this.cart.longestItem,
          largestItemCircumference: this.cart.largestItemCircumference,
          largestItemVolume: this.cart.largestItemVolume,
          transportLabeling: this.cart.transportLabeling
        }
      ).then(response => {
        this.shippingMethods = response.data

        if (this.loading) {
          this.selectedShipping = null
          this.selectedAgentId = null
          this.selectedAgent = null

          if (this.checkoutState.selectedShipping) {
            let substitutedSelectedShipping = this.checkoutState.selectedShipping

            let shippingMethodsFlat = _.flatMap(this.shippingMethods, 'items')
            let shipping = _.find(shippingMethodsFlat, (o) => { return o.optionId === this.checkoutState.selectedShipping })

            if (!shipping) {
              shipping = _.find(shippingMethodsFlat, (o) => { return o.preferredDeliveryGroupingKey === this.checkoutState.preferredDeliveryGroupingKey })

              if (shipping) {
                substitutedSelectedShipping = shipping.optionId
              }
            }

            if (shipping) {
              this.selectedShipping = substitutedSelectedShipping
              if (shipping.agents) {
                this.deliveryAgents = shipping.agents
              }
            } else {
              // Checkout state selected shipping is not valid any more
              this.checkoutState.selectedShipping = null
              this.checkoutState.selectedAgent = null
            }

            if (!this.isOrderRequestVisitorState) {
              this.selectedAgentId = this.checkoutState.selectedAgent
              this.selectedAgent = _.find(this.deliveryAgents, (x) => { return x.agentId === this.selectedAgentId })
              this.previousSelectedShipping = this.selectedShipping
            }
          }
          this.loading = false
        } else if (!this.selectedShipping) {
          this.selectedAgentId = null
          this.selectedAgent = null
        }
      }).catch((e) => {
        this.shippingMethods = []
      })
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    closeDropdown() {
      this.isDropdownOpen = false
    },
    hasAgents(method) {
      if(method.suppressAgentList) {
        return false
      }
      return method.optionId === this.selectedShipping && method.deliveryType === 'agent'
    },
    isAgentSelected(id) {
      return this.selectedAgent !== null && this.selectedAgent !== undefined && this.selectedAgent.agentId === id
    },
    pushGaData() {
      if (this.enableGtm) {
        this.publish('PUSH_MANUAL_SELECTED_PAYMENT')
      }
    },
    setNotificationPayload() {
      this.publish('checkout-container-notification', { notificationEmail: this.notificationEmail, notificationPhone: this.notificationPhone })
    },
    formatNotificationPhone() {
      let countryCode = null
      if (this.customer.anonymous) {
        countryCode = this.shippingAddress.countryCode
      } else {
        countryCode = this.customer.invoiceAddress.countryCode
      }
      if (this.formatingProperties.phone && countryCode !== null) {
        const currentCountry = this.clientModel.countryList.find(c => c.value === countryCode)

        if (currentCountry) {
          this.notificationPhone = utils.savePhoneNumberWithFormating(this.formatingProperties.phone, currentCountry.callingCode)
          this.formatingProperties.phone = utils.extractPhoneNumberFromFormating(this.formatingProperties.phone, currentCountry.callingCode)
        }
        this.setNotificationPayload()
      } else if (this.formatingProperties.phone !== null && this.formatingProperties.phone.length === 0 && this.notificationPhone) {
        this.notificationPhone = null
        this.setNotificationPayload()
      }
    },
    resetShippingStep() {
      this.selectedShipping = null
      this.selectedAgentId = null
      this.selectedAgent = null
      this.isSelectedResetFromEvent = true
      this.isNextStepAvailable = false
      this.isOpen = false
      this.notificationEmail = null
      this.notificationPhone = null
      this.useShippingAgreement = false
    },
    getDeliveryMethodPrice(method) {
      return method.useShippingAgreement ? 0 : method.price
    }
  }
}
</script>

<style lang="scss">
.checkout-shipping-type__title {
  @include h3-semi-bold;
  margin-top: 0;
  margin-bottom: 0 !important;
}
.checkout-shipping-notification__title {
  @include h3-semi-bold;
  margin-top: 0;
  margin-bottom: measure(quarter);
}

.checkout-shipping-type__description {
  @include body;
  margin-top: 0;
}

.checkout-shipping-type .radio__label-main-text {
  @include body-semi-bold;
}

.checkout-shipping-type__shipping-agreement, .checkout-shipping-type__agents {
  margin-top: measure(half);
  margin-left: measure(double);
}

.checkout-shipping-type__shipping-agreement {
  width: fit-content;
}

.checkout-shipping-type__shipping-agreement>*:not(:last-child) {
  margin-bottom: measure(quarter);
}

.checkout-shipping-type__agents {
  display: inline-block;
}

.checkout-shipping-type__radio .radio__label {
  &::before {
    align-self: start;
  }

  &::after {
    align-self: start;
    top: 0.4375rem;
    transform: translateY(0) translateX(0) !important;
  }
}
</style>
